import { DropdownValue, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, DropdownProps, Grid, Loader, Modal, Table } from 'semantic-ui-react';
import { getAllTariffs } from 'services/tariffServices';
import axios, { CancelTokenSource } from 'axios';
import { deleteCostEstimation, getAllPatientCostEstimationsByTariffId } from 'services/costEstimationServices';
import styled from 'styled-components';
import { CostEstimationDto, TariffDto } from 'ts-types/api.types';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import ToolbarButton from 'components/layout/ToolbarButton';
import { debounce } from 'ts-debounce';
import moment from 'moment';
import UpsertCostEstimation from 'components/costestimation/UpsertCostEstimation';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';
import { EntriesConsumerRenderProps, withEntriesContext } from 'components/segments/entires/EntriesContext';
import { noop } from 'utils/functionUtils';
import { clearProceduresSearchCache } from 'services/searchProceduresServices';
import { errorUtils } from 'utils/errorUtils';
import { PxDropdownSc } from 'components/final-form/Select';
import AddButton from 'components/AddButton';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';

const ScrollableTableContainer = styled.div`
  height: 350px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  border-radius: .28571429rem;

  & .ui.table {
    border: none !important;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: -1px;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34,36,38,.15);
  }
`;

const StyledButtons = styled(Table.Cell)`

  & .ui.icon.button, .ui.icon.buttons .button {
    padding-top: 5px !important;
    padding-left: 0px !important;
}

`;

interface Props extends MessageSourceProps, PatientConsumerRenderProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  setCacheSearchId: (searchId?: string) => string,
  patientId: number
}

interface State {
  tariffId: number,
  tariffs: Array<TariffDto>,
  costEstimations: Array<CostEstimationDto>,
  searchId?: string,
  loadingData: boolean,
  loadingTableData: boolean,
  cancelTokenSource: CancelTokenSource,
  upsertCostEstimationOpen: boolean,
  costEstimationId?: number,
  readOnly: boolean,
  errorMessage?: string
}

class PatientCostEstimationsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      tariffId: -1,
      tariffs: [],
      costEstimations: [],
      loadingData: true,
      loadingTableData: false,
      cancelTokenSource: cancelTokenSource,
      upsertCostEstimationOpen: false,
      readOnly: false,
      errorMessage: undefined
    };

    getAllTariffs(cancelTokenSource)
        .then(response => {
          this.setState({
            tariffs: response,
            tariffId: response.length ? response[0].id : -1
          });

          if (response.length) {
            this.getPatientCostEstimations(response[0].id);
          }
        })
        .catch(noop)
        .finally(() => {
          this.setState({
            loadingData: false
          })
        })
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  getPatientCostEstimations = (tariffId: number) => {
    if (!this.mounted) {
      return;
    }

    const {patientId} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      loadingTableData: true
    });

    getAllPatientCostEstimationsByTariffId(patientId, tariffId, cancelTokenSource)
        .then(response => {
          this.setState({
            costEstimations: response
          })
        })
        .catch(noop)
        .finally(() => {
          this.setState({
            loadingTableData: false
          })
        });
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.costEstimationInUse];
    let errorMessage = "";

    if (knownErrors.includes(errorCode)) {
      errorMessage = getMessage(`error.${errorCode}`)
    } else {
      errorMessage = getMessage('error.general');
    }

    this.setErrorMessage(errorMessage)
  }

  onOpenUpsertCostEstimationModal = () => {
    this.setState(() => ({
      upsertCostEstimationOpen: true
    }));
  };

  onCloseUpsertCostEstimationModal = () => {
    const {upsertCostEstimationOpen, searchId, cancelTokenSource} = this.state;
    const {setCurrentEntries} = this.props;

    if (searchId) {
      clearProceduresSearchCache(searchId, cancelTokenSource)
          .then(response => response)
          .catch(noop);
    }

    if (upsertCostEstimationOpen) {
      setCurrentEntries([]);
    }

    this.setState(() => ({
      searchId: undefined,
      upsertCostEstimationOpen: false
    }));
  };

  onCostEstimationDetailInformation = (costEstimationId: number) => {

    this.setState({
      readOnly: true,
      costEstimationId: costEstimationId
    });

    this.onOpenUpsertCostEstimationModal();
  };

  onUpsertCostEstimation = (costEstimationId?: number) => {

    this.setState({
      readOnly: false,
      costEstimationId: costEstimationId
    });

    this.onOpenUpsertCostEstimationModal();
  };

  onConfirmUpsertCostEstimation = (newCostEstimation: CostEstimationDto) => {

    const {costEstimations} = this.state;

    const foundCostEstimationIndex = costEstimations
        .findIndex(costEstimation => costEstimation.id == newCostEstimation.id);

    if (foundCostEstimationIndex != -1) {

      const modifiedCostEstimations = Array.from(costEstimations);

      modifiedCostEstimations[foundCostEstimationIndex] = {
        ...newCostEstimation
      };

      this.setState({
        costEstimations: modifiedCostEstimations
      })
    } else {
      this.setState({
        costEstimations: [...costEstimations, newCostEstimation]
      })
    }
  };

  onDeleteCostEstimation = (costEstimationId: number) => {
    const {costEstimations, cancelTokenSource} = this.state;

    deleteCostEstimation(costEstimationId, cancelTokenSource)
        .then(response => {
          const modifiesCostEstimations =
              costEstimations.filter(costEstimation => costEstimation.id != costEstimationId);

          this.setState({
            costEstimations: modifiesCostEstimations
          })
        })
        .catch(error => this.handleError(error.response.data));
  };

  onTariffChange = debounce((value?: DropdownValue) => {

    this.setState({
      tariffId: value as number
    });

    this.getPatientCostEstimations(value as number);
  }, 300);

  setCacheSearchId = (searchId?: string): void => {
    this.setState({
      searchId
    })
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {loadingData} = this.state;

    return loadingData ? this.renderLoader() : this.renderModal();
  }

  renderModal = (): React.ReactNode => {

    const {modalOpen, onCloseModal, getMessage, currentPatient} = this.props;
    const {tariffId, tariffs, costEstimationId, upsertCostEstimationOpen, readOnly, errorMessage} = this.state;

    const mappedTariffs = mapToDropdownOptionArray(tariffs, "description");
    const foundTariff = tariffs.find(tariff => tariff.id == tariffId);

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{height: "450px", width: "350px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("costEstimations")}</Modal.Header>
          <FormModalContentContainer>
            {errorMessage
            && (
                <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                  {errorMessage}
                </StyledErrorMessage>
            )
            }
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={3} verticalAlign="middle">{getMessage("tariff")}:</Grid.Column>
                <Grid.Column width={13}>
                  <PxDropdownSc
                      value={tariffId}
                      onChange={(e: any, data: DropdownProps) => this.onTariffChange(data.value)}
                      options={mappedTariffs}
                      selection
                      search
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.onUpsertCostEstimation()}/>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <ScrollableTableContainer>
                    {this.renderTable()}
                  </ScrollableTableContainer>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
            {upsertCostEstimationOpen &&
            <UpsertCostEstimation
                modalOpen={upsertCostEstimationOpen}
                onCloseModal={this.onCloseUpsertCostEstimationModal}
                onConfirmAction={this.onConfirmUpsertCostEstimation}
                costEstimationId={costEstimationId}
                patientId={currentPatient ? currentPatient.id : -1}
                tariffId={foundTariff ? foundTariff.id : -1}
                tarmedTariff={foundTariff ? foundTariff.tarmedTariff : false}
                setCacheSearchId={this.setCacheSearchId}
                readOnly={readOnly}
            />
            }
          </FormModalContentContainer>
          <Modal.Actions>
            <Button compact onClick={onCloseModal}>{getMessage("button.close")}</Button>
          </Modal.Actions>
        </Modal>
    );
  };

  renderTable = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {costEstimations, loadingTableData} = this.state;

    const mappedCostEstimations = costEstimations.map(costEstimation => (
        <Table.Row key={costEstimation.id}>
          <Table.Cell
              disabled={costEstimation.used}>{moment(costEstimation.estimationDate).format("DD.MM.YYYY")}</Table.Cell>
          <StyledButtons>
            {
              costEstimation.used ? (
                      <ToolbarButton iconName="ellipsis horizontal"
                                     onClick={() => this.onCostEstimationDetailInformation(costEstimation.id)}
                                     dataTooltip={getMessage("costEstimation.detailInfo")}
                      />
                  )
                  : (
                      <React.Fragment>
                        <ToolbarButton iconName="edit outline"
                                       onClick={() => this.onUpsertCostEstimation(costEstimation.id)}
                                       dataTooltip={getMessage("costEstimation.edit")}
                        />
                        <ConfirmActionPopup
                            message={getMessage("costEstimation.confirmDelete")}
                            renderTarget={this.renderDeleteTrigger()}
                            onConfirmAction={() => this.onDeleteCostEstimation(costEstimation.id)}
                        />
                      </React.Fragment>
                  )
            }
          </StyledButtons>
        </Table.Row>
    ));

    return (
        <Table celled size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={5}>{getMessage("costEstimation.date")}</Table.HeaderCell>
              <Table.HeaderCell width={2}>{getMessage("actions")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loadingTableData ? this.renderTableLoader() : mappedCostEstimations}
          </Table.Body>
        </Table>
    )
  };

  renderDeleteTrigger = (): React.ReactNode => {

    const {getMessage} = this.props;

    return <ToolbarButton iconName="trash" dataTooltip={getMessage("costEstimation.delete")}/>
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active page>
          <Loader inline='centered' size="medium" inverted>{getMessage("costEstimation.loading")}</Loader>
        </Dimmer>
    )
  };

  renderTableLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Table.Row>
          <Table.Cell>
            <Dimmer active inverted>
              <Loader inline='centered' size="medium">{getMessage("costEstimation.loadingTable")}</Loader>
            </Dimmer>
          </Table.Cell>
        </Table.Row>
    )
  };
}

export default withMessages(withPatientContext(withEntriesContext(PatientCostEstimationsView)));
